import type { ChangeEvent, FunctionComponent } from 'react';
import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { getNonPublicIfAvailable } from '@trello/business-logic/member';
import { sendErrorEvent } from '@trello/error-reporting';
import { Button } from '@trello/nachos/button';
import type { CheckboxProps } from '@trello/nachos/checkbox';
import { Checkbox } from '@trello/nachos/checkbox';
import { showFlag } from '@trello/nachos/experimental-flags';
import { CloseIcon } from '@trello/nachos/icons/close';
import { HelpIcon } from '@trello/nachos/icons/help';
import { OrganizationIcon } from '@trello/nachos/icons/organization';
import { Tooltip } from '@trello/nachos/tooltip';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { buildFuzzyMatcher } from '@trello/strings';
import type { WorkspaceRequestAccessTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';
import { token } from '@trello/theme';

import {
  useWorkspaceJoinRequestPopover,
  WorkspaceJoinRequestPopover,
} from 'app/src/components/WorkspaceJoinRequestPopover';
import { useApproveWorkspaceJoinRequestMutation } from './ApproveWorkspaceJoinRequestMutation.generated';
import { useDeleteWorkspaceJoinRequestMutation } from './DeleteWorkspaceJoinRequestMutation.generated';
import { useWorkspaceJoinRequestItemQuery } from './WorkspaceJoinRequestItemQuery.generated';
import { WorkspaceJoinRequestListDocument } from './WorkspaceJoinRequestListQuery.generated';

import styles from './WorkspaceJoinRequestItem.less';

interface WorkspaceJoinRequestItemProps {
  requestWorkspaceAccessDate: string;
  memberId: string;
  checkboxes?: {
    checkboxState: CheckboxProps['checkboxState'];
    setCheckboxState: CheckboxProps['setCheckboxState'];
  };
  orgId: string;
  signature: string;
  orgType: 'bc' | 'enterprise' | 'free' | 'standard';
  showErrorFlag: (error: string, user: string, workspaceId: string) => void;
  showSuccessFlag: (user: string) => void;
  orgIdEnterprise?: string;
  orgEnterpriseName?: string;
  nameFilter?: string;
  isMemberWorkspaceGuest: boolean;
  onCheckboxChange: (action: 'checked' | 'unchecked') => void;
  isAddToWorkspaceEnabled: boolean;
  isWorkspaceOverLimit: boolean;
}

export const WorkspaceJoinRequestItem: FunctionComponent<
  WorkspaceJoinRequestItemProps
> = ({
  memberId,
  signature,
  requestWorkspaceAccessDate,
  checkboxes,
  orgId,
  orgType,
  showErrorFlag,
  showSuccessFlag,
  orgIdEnterprise,
  orgEnterpriseName,
  nameFilter,
  isMemberWorkspaceGuest,
  onCheckboxChange,
  isAddToWorkspaceEnabled,
  isWorkspaceOverLimit,
}) => {
  const intl = useIntl();

  const { data } = useWorkspaceJoinRequestItemQuery({
    variables: { id: memberId },
    waitOn: ['None'],
  });
  const member = data?.member;
  const username = member?.username;
  const fullName = member
    ? getNonPublicIfAvailable(member, 'fullName')
    : undefined;

  const avatarUrl = member
    ? getNonPublicIfAvailable(member, 'avatarUrl')
    : undefined;
  const isDisabled = false;

  const shouldShowRequest = useMemo(() => {
    return (
      !nameFilter ||
      buildFuzzyMatcher(nameFilter)(
        dangerouslyConvertPrivacyString(fullName) ?? '',
      )
    );
  }, [nameFilter, fullName]);

  const {
    triggerRefApproveWorkspaceJoinRequestPopover,
    toggleApproveWorkspaceJoinRequestPopover,
    hideApproveWorkspaceJoinRequestPopover,
    approveWorkspaceJoinRequestPopoverProps,
    triggerRefDeletePopover,
    toggleDeletePopover,
    hideDeletePopover,
    deletePopoverProps,
  } = useWorkspaceJoinRequestPopover();

  const [approveAccessRequest] = useApproveWorkspaceJoinRequestMutation({
    refetchQueries: [
      {
        query: WorkspaceJoinRequestListDocument,
        variables: { orgId },
        context: {
          operationName: 'RefetchWorkspaceJoinRequestList',
          document: WorkspaceJoinRequestListDocument,
        },
      },
    ],
  });
  const [deleteAccessRequest] = useDeleteWorkspaceJoinRequestMutation({
    refetchQueries: [
      {
        query: WorkspaceJoinRequestListDocument,
        variables: { orgId },
        context: {
          operationName: 'RefetchWorkspaceJoinRequestList',
          document: WorkspaceJoinRequestListDocument,
        },
      },
    ],
  });

  const addMemberToWorkspace = useCallback(
    async (onComplete?: () => void) => {
      const taskName = 'edit-organization/members/add';
      const source = 'workspaceRequestsScreen';
      const traceId = Analytics.startTask({
        taskName,
        source,
      });

      const dangerousUsername = dangerouslyConvertPrivacyString(username) ?? '';

      const user =
        dangerouslyConvertPrivacyString(fullName) !== ''
          ? `${dangerouslyConvertPrivacyString(fullName)} ${dangerousUsername}`
          : dangerousUsername;

      try {
        await approveAccessRequest({
          variables: {
            workspaceId: orgId,
            user: { id: memberId },
            traceId,
          },
        });
        Analytics.taskSucceeded({
          taskName,
          source,
          traceId,
        });
        Analytics.sendTrackEvent({
          action: 'added',
          actionSubject: 'member',
          source,
          containers: {
            organization: {
              id: orgId,
            },
          },
          attributes: {
            requestor: memberId,
          },
        });

        showSuccessFlag(user);
      } catch (error) {
        Analytics.taskFailed({
          taskName,
          source,
          error,
          traceId,
        });

        // @ts-expect-error
        showErrorFlag(error.message, user, orgId);
        sendErrorEvent(error, {
          tags: {
            ownershipArea: 'trello-web-eng',
            feature: 'Workspace Membership Request',
          },
          extraData: {
            component: 'WorkspaceJoinRequestItem',
          },
        });
      }
      if (onComplete) onComplete();
    },
    [
      approveAccessRequest,
      username,
      memberId,
      orgId,
      showErrorFlag,
      showSuccessFlag,
      fullName,
    ],
  );

  // Opens popover for confirmation
  const onClickAddToWorkspace = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addToWorkspaceButton',
      source: 'workspaceRequestsScreen',
      containers: formatContainers({ workspaceId: orgId }),
      attributes: {
        requestor: memberId,
      },
    });

    if (orgType !== 'free') {
      // opens popover
      toggleApproveWorkspaceJoinRequestPopover();
    } else {
      addMemberToWorkspace();
    }
  }, [
    orgId,
    toggleApproveWorkspaceJoinRequestPopover,
    addMemberToWorkspace,
    memberId,
    orgType,
  ]);

  // confirms accepting requests
  const onConfirmAddToWorkspace = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'addToWorkspaceButton',
      source: 'approvePendingMembersInlineDialog',
      containers: formatContainers({ workspaceId: orgId }),
      attributes: {
        requestor: memberId,
      },
    });

    addMemberToWorkspace(toggleApproveWorkspaceJoinRequestPopover);
  }, [
    orgId,
    toggleApproveWorkspaceJoinRequestPopover,
    addMemberToWorkspace,
    memberId,
  ]);

  const onHideAddToWorkspacePopover = useCallback(() => {
    hideApproveWorkspaceJoinRequestPopover();
  }, [hideApproveWorkspaceJoinRequestPopover]);

  // Opens popover for deleting requests
  const onClickDelete = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'deleteButton',
      source: 'workspaceRequestsScreen',
      containers: formatContainers({ workspaceId: orgId }),
    });
    // closes popover
    toggleDeletePopover();
  }, [orgId, toggleDeletePopover]);

  // confirms deleting requests
  const onConfirmDelete = useCallback(async () => {
    const source = 'deletePendingMembersInlineDialog';
    const taskName = 'delete-requestAccess/organization';
    const traceId = Analytics.startTask({
      taskName,
      source,
    });

    Analytics.sendClickedButtonEvent({
      buttonName: 'deleteButton',
      source,
      containers: formatContainers({ workspaceId: orgId }),
    });

    try {
      await deleteAccessRequest({
        variables: {
          traceId,
          modelType: 'organization',
          id: orgId,
          idInvitedMember: memberId,
          signature,
        },
      });

      Analytics.taskSucceeded({
        taskName,
        source,
        traceId,
      });

      Analytics.sendTrackEvent({
        action: 'deleted',
        actionSubject: 'workspaceAccessRequest',
        source,
        containers: {
          organization: {
            id: orgId,
          },
        },
        attributes: {
          requestor: memberId,
        },
      });
    } catch (error) {
      Analytics.taskFailed({
        taskName,
        source,
        error,
        traceId,
      });

      showFlag({
        id: 'deleteRequestAccessToJoinError',
        title: intl.formatMessage({
          id: 'templates.workspace_join_requests.could-not-connect-to-trello',
          defaultMessage: 'Could not connect to Trello.',
          description: 'Error message when unable to connect to Trello',
        }),
        appearance: 'error',
        actions: [
          {
            content: intl.formatMessage({
              id: 'templates.workspace_join_requests.reload-page',
              defaultMessage: 'Reload page',
              description: 'Action to reload the page',
            }),
            onClick: () => window.location.reload(),
            type: 'link',
          },
        ],
      });

      sendErrorEvent(error, {
        tags: {
          ownershipArea: 'trello-web-eng',
          feature: 'Workspace Membership Request',
        },
        extraData: {
          component: 'WorkspaceJoinRequestItem',
        },
      });
    }
    // closes popover
    toggleDeletePopover();
  }, [
    orgId,
    toggleDeletePopover,
    deleteAccessRequest,
    memberId,
    signature,
    intl,
  ]);

  const onHideDeletePopover = useCallback(() => {
    hideDeletePopover();
  }, [hideDeletePopover]);

  const onClickCheckbox = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const action = e.target.checked ? 'checked' : 'unchecked';
      onCheckboxChange(action);

      Analytics.sendUIEvent({
        action,
        actionSubject: 'checkbox',
        actionSubjectId: 'pendingMemberCheckbox',
        source: 'workspaceRequestsScreen',
        containers: formatContainers({ workspaceId: orgId }),
      });
    },
    [orgId, onCheckboxChange],
  );

  const getAddToWorkspaceTooltip = useCallback(() => {
    if (!isAddToWorkspaceEnabled && isWorkspaceOverLimit) {
      return (
        <Tooltip
          content={intl.formatMessage({
            id: 'templates.workspace_join_requests.this-workspace-is-over-the-limit',
            defaultMessage:
              'This free Workspace is over the 10 collaborator limit. Upgrade this Workspace for unlimited collaborators.',
            description:
              'Tooltip for add member to workspace button when already over the limit',
          })}
          tag="span"
          position="top"
        >
          <HelpIcon />
        </Tooltip>
      );
    }
    if (!isAddToWorkspaceEnabled) {
      return (
        <Tooltip
          content={intl.formatMessage({
            id: 'templates.workspace_join_requests.adding-this-person-would-exceed-limit',
            defaultMessage:
              'Adding this person would exceed the free Workspace 10 collaborator limit. Upgrade this Workspace for unlimited collaborators.',
            description: 'Tooltip for add member to workspace button',
          })}
          tag="span"
          position="top"
        >
          <HelpIcon />
        </Tooltip>
      );
    }
    return null;
  }, [intl, isAddToWorkspaceEnabled, isWorkspaceOverLimit]);

  if (!shouldShowRequest) {
    return null;
  }

  return (
    <div
      className={styles.guestMemberListItem}
      data-testid={getTestId<WorkspaceRequestAccessTestIds>(
        'ws-join-request-item',
      )}
    >
      <div className={styles.leftSide}>
        <Checkbox
          {...checkboxes}
          className={styles.checkBox}
          isDisabled={isDisabled}
          aria-label={intl.formatMessage(
            {
              id: 'templates.workspace_join_requests.select-name',
              defaultMessage: 'Select {name}',
              description:
                'Aria label for checkbox to select a user join request',
            },
            {
              name: dangerouslyConvertPrivacyString(fullName),
            },
          )}
          onChange={onClickCheckbox}
          value={memberId}
        />
        <div className={styles.iconBackground}>
          {avatarUrl ? (
            <img
              className={styles.memberAvatar}
              height="32"
              width="32"
              alt={`${dangerouslyConvertPrivacyString(fullName)} avatar`}
              title="avatar"
              src={[avatarUrl, '50.png'].join('/')}
              srcSet={
                [avatarUrl, '50.png 1x'].join('/') +
                ', ' +
                [avatarUrl, '50.png 2x'].join('/')
              }
            />
          ) : (
            <OrganizationIcon
              dangerous_className={styles.icon}
              color={token('color.icon', '#44546F')}
              size="large"
              block={true}
            />
          )}
        </div>

        <div data-testid="details" className={styles.details}>
          <div>
            <span className={styles.fullName}>{fullName}</span>
          </div>
          <div className={styles.subDetails}>
            <p>
              {username && (
                <span className={styles.userNameOrEnterprise}>@{username}</span>
              )}
              <span className={styles.bulletSeparator}>&#x2022;</span>
              <span>
                {isMemberWorkspaceGuest ? (
                  <FormattedMessage
                    id="templates.workspace_join_requests.workspace-guest"
                    defaultMessage="Workspace guest"
                    description="Workspace guest"
                  />
                ) : (
                  <FormattedMessage
                    id="templates.workspace_join_requests.not-in-this-workspace"
                    defaultMessage="Not in this Workspace"
                    description="Not in this Workspace"
                  />
                )}
              </span>
            </p>
            {orgEnterpriseName && (
              <p>
                {orgIdEnterprise === member?.idEnterprise ? (
                  <FormattedMessage
                    id="templates.select_member.managed-by-enterprise"
                    defaultMessage="Managed by {enterpriseName}"
                    description="This user is managed by the enterprise"
                    values={{
                      enterpriseName: orgEnterpriseName,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="templates.select_member.not-managed-by-enterprise"
                    defaultMessage="Not managed by {enterpriseName}"
                    description="This user is not managed by the enterprise"
                    values={{
                      enterpriseName: orgEnterpriseName,
                    }}
                  />
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className={styles.rightSide}>
        <p className={styles.requestWorkspaceAccessDate}>
          {intl.formatMessage(
            {
              id: 'templates.workspace_join_requests.request-sent',
              defaultMessage: 'Request sent {date}',
              description: 'The date the request was sent',
            },
            {
              date: requestWorkspaceAccessDate,
            },
          )}
        </p>
        <Button
          ref={triggerRefApproveWorkspaceJoinRequestPopover}
          onClick={onClickAddToWorkspace}
          aria-label={intl.formatMessage(
            {
              id: 'templates.workspace_join_requests.approve-name-request-to-join-workspace',
              defaultMessage: 'Approve {name} request to join Workspace.',
              description: 'Button label to approve request to join workspace',
            },
            {
              name: dangerouslyConvertPrivacyString(fullName),
            },
          )}
          data-testid={getTestId<WorkspaceRequestAccessTestIds>(
            'ws-join-request-approve-button',
          )}
          isDisabled={!isAddToWorkspaceEnabled}
          className={styles.addToWorkspaceButton}
        >
          {intl.formatMessage({
            id: 'templates.workspace_join_requests.add-to-workspace',
            defaultMessage: 'Add to Workspace',
            description: 'Button text to add member to workspace',
          })}
          {getAddToWorkspaceTooltip()}
        </Button>
        <WorkspaceJoinRequestPopover
          {...approveWorkspaceJoinRequestPopoverProps}
          onClick={onConfirmAddToWorkspace}
          isMultipleSelected={false}
          isDelete={false}
          onHide={onHideAddToWorkspacePopover}
          orgType={orgType}
          orgId={orgId}
        />
        <Button
          onClick={onClickDelete}
          ref={triggerRefDeletePopover}
          aria-label={intl.formatMessage(
            {
              id: 'templates.workspace_join_requests.delete-name-request-to-join-workspace',
              defaultMessage: 'Delete {name} request to join Workspace.',
              description: 'Button label to delete request to join workspace',
            },
            {
              name: dangerouslyConvertPrivacyString(fullName),
            },
          )}
          iconBefore={
            <CloseIcon
              label={intl.formatMessage({
                id: 'templates.workspace_join_requests.delete-request',
                defaultMessage: 'Delete request',
                description: 'Icon label to delete join request',
              })}
              // Overrides browser for setting auto on min properties that shrinks the icon and making it disappear on smaller medias
              dangerous_className={styles.removeButton}
            />
          }
          appearance="subtle"
          data-testid={getTestId<WorkspaceRequestAccessTestIds>(
            'ws-join-request-delete-button',
          )}
        />
        <WorkspaceJoinRequestPopover
          {...deletePopoverProps}
          onClick={onConfirmDelete}
          isMultipleSelected={false}
          isDelete={true}
          onHide={onHideDeletePopover}
          orgType={orgType}
          orgId={orgId}
        />
      </div>
    </div>
  );
};
